//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { useEffect, useRef, useState } from 'react';
import MCPDataService, { IngestJob } from '../../utils/MCPDataService';
import { subscribeIngestionTriggered } from './ingestionTriggerBus';

// 1500ms while active generated enough request volume (each poll resolves the
// teamspace's effective integration server-side) to flood analyticsai-server's
// logs during a real ingest run. 3000ms roughly halves that load while still
// feeling responsive for job-progress UI; 15s idle was already reasonable and
// is unchanged.
export const ACTIVE_POLL_INTERVAL_MS = 3000;
export const IDLE_POLL_INTERVAL_MS = 15000;

export interface IngestJobsPollingResult {
  jobs: IngestJob[];
  error: string | null;
}

// GET /api/mcp/catalog/jobs deliberately returns 403 "No enabled integration
// for this teamspace" when the teamspace has no AI integration configured —
// see analyticsai-server's list_manual_catalog_jobs. That's the normal state
// for a teamspace that has never set one up, not a failure, so it must not
// surface as a user-facing error (which would otherwise show unconditionally
// on every page that mounts this hook, integration or not).
function isNoEnabledIntegrationResponse(pollError: unknown): boolean {
  return (
    typeof pollError === 'object' &&
    pollError !== null &&
    (pollError as { status?: unknown }).status === 403
  );
}

// Failures that can clear up on their own, so the poller must keep retrying
// instead of stopping for good. That covers 5xx (analyticsai restarting, or
// tenant-server unreachable while it verifies teamspace access), 401 (token
// mid-refresh), 408/429, and rejections with no HTTP status at all:
// MCPDataService rejects with `error.response`, which is undefined for a
// network error, and with the string 'User is not logged in.' before auth has
// settled. A permanent stop here once left the navbar Loop Genie — which
// mounts at page load and never remounts — unblocked for the rest of the
// session after a single 503.
function isRetryablePollError(pollError: unknown): boolean {
  const status =
    typeof pollError === 'object' && pollError !== null
      ? (pollError as { status?: unknown }).status
      : undefined;
  if (typeof status !== 'number') {
    return true;
  }
  return status >= 500 || status === 401 || status === 408 || status === 429;
}

/**
 * Polls GET /api/mcp/catalog/jobs while mounted and a teamspaceId is
 * present. Deliberately has no "active"/trigger flag and never stops on its
 * own while idle: any mounted instance must keep watching so it can pick up
 * a job triggered by a DIFFERENT component (MCPTab, SidePanel, Integration)
 * at any time. To keep that always-watching guarantee cheap, the interval
 * adapts — fast while there are jobs to track, slow once idle. Transient
 * request errors (see isRetryablePollError) are retried at the idle cadence;
 * only a non-retryable 4xx (e.g. 400 for a bad request) stops polling
 * permanently.
 *
 * Also subscribes to the ingestionTriggerBus for this teamspaceId: a host
 * component calls notifyIngestionTriggered() right after its own
 * catalogIngest/triggerIngestion call resolves, so an already-idle poll
 * loop refreshes immediately instead of waiting out its current (up to
 * 15s) backoff before it notices the new job.
 */
export function useIngestJobsPolling(
  mcpDataService: Pick<MCPDataService, 'listIngestJobs'>,
  teamspaceId: string | undefined,
  // Fires once per distinct failed job — not once per poll — so a caller can
  // surface a durable, one-shot notification (e.g. a toast) the moment a job
  // fails, instead of relying entirely on the reader having the right panel
  // open at the right instant. Backend now keeps failed jobs listed for
  // minutes (see analyticsai-server's _FAILED_JOB_RETENTION_SECONDS), but a
  // long retention window only helps if something proactively points the
  // reader at it.
  onJobFailed?: (job: IngestJob) => void,
): IngestJobsPollingResult {
  const [jobs, setJobs] = useState<IngestJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const onJobFailedRef = useRef(onJobFailed);

  useEffect(() => {
    onJobFailedRef.current = onJobFailed;
  }, [onJobFailed]);

  useEffect(() => {
    // Resetting jobs/error here is an intentional "restart tracking for a
    // new target" reset whenever teamspaceId/mcpDataService changes, not an
    // effect-derived-state anti-pattern — the poll loop below is the actual
    // subscription and is what keeps state in sync going forward.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setJobs([]);
    setError(null);
    if (!teamspaceId) {
      return undefined;
    }

    let cancelled = false;
    let stopped = false;
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    // Scoped to this effect run (reset whenever teamspaceId/mcpDataService
    // changes) rather than a ref, since "which jobs have we already notified
    // about" is only meaningful for the current subscription's lifetime.
    const notifiedFailedJobIds = new Set<string>();

    const poll = async () => {
      if (cancelled || stopped) return;
      try {
        const response = await mcpDataService.listIngestJobs(teamspaceId);
        if (cancelled) return;
        setJobs(response.jobs);
        // Clears an error left by an earlier, since-recovered failure.
        setError(null);
        for (const job of response.jobs) {
          if (
            job.status === 'failed' &&
            !notifiedFailedJobIds.has(job.job_id)
          ) {
            notifiedFailedJobIds.add(job.job_id);
            onJobFailedRef.current?.(job);
          }
        }
        const nextDelay =
          response.jobs.length > 0
            ? ACTIVE_POLL_INTERVAL_MS
            : IDLE_POLL_INTERVAL_MS;
        timeoutId = setTimeout(poll, nextDelay);
      } catch (pollError) {
        if (cancelled) return;
        if (isNoEnabledIntegrationResponse(pollError)) {
          // Nothing to show yet, not a failure — keep watching at the idle
          // cadence (same as a normal "no jobs" result) so the panel picks
          // up ingestion as soon as an integration is added and triggered,
          // without ever having shown an error for a teamspace that never
          // had one configured.
          setJobs([]);
          timeoutId = setTimeout(poll, IDLE_POLL_INTERVAL_MS);
          return;
        }
        // eslint-disable-next-line no-console
        console.error(
          '[useIngestJobsPolling] listIngestJobs failed:',
          pollError,
        );
        setError('Unable to load ingestion status');
        if (isRetryablePollError(pollError)) {
          // Keep the last known jobs rather than clearing them, so a blip in
          // the middle of an ingestion run can't briefly unblock Loop Genie.
          // pollNow (the trigger bus) still forces an immediate retry.
          timeoutId = setTimeout(poll, IDLE_POLL_INTERVAL_MS);
          return;
        }
        stopped = true;
      }
    };

    const pollNow = () => {
      if (cancelled || stopped) return;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      poll();
    };

    poll();
    const unsubscribe = subscribeIngestionTriggered(teamspaceId, pollNow);

    return () => {
      cancelled = true;
      unsubscribe();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [mcpDataService, teamspaceId]);

  return { jobs, error };
}
